import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  UserPlus, Trash2, Edit3, ShieldCheck, X, Save, RotateCcw, Loader2,
  ChevronDown, ChevronRight, KeyRound, Warehouse, ClipboardList, ScanLine, Eye,
  Copy, Search,
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import MobileFixedActions from '../components/MobileFixedActions';
import { useAdminAuth, API } from '../context/AdminAuthContext';
import { useToast } from '../hooks/use-toast';
import { Switch } from '../components/ui/switch';

/**
 * AdminUsers — super-admin-only page to create & manage sub-admin accounts.
 *
 * Permission model (Feb 2026 rework):
 * - Each module (Products, Orders, …) exposes a set of actions.
 * - The Module checkbox toggles the bare module slug = read-only access.
 *   Expanding it reveals action-level checkboxes (Create, Edit, Delete,
 *   Bulk edit, …). Each checked action becomes a `{module}.{action}` slug
 *   in the stored permissions list.
 * - If the user has the bare module but no actions, they get read-only.
 * - Legacy sub-admins (created before this rework) only have bare module
 *   slugs — the backend treats those as "all actions in that module" so
 *   they keep their previous access.
 */
// Feb 2026 — Per-weekday duty schedule. Mon=0 … Sun=6 (matches
// Python's datetime.weekday()). The admin user edit modal renders a
// 7-row grid; each row carries { start, end, off }. Blank
// start/end on an on-duty day means "no window" (always-on that
// day), matching the backend evaluator.
const WEEKDAY_META = [
  { v: 0, l: 'Mon' },
  { v: 1, l: 'Tue' },
  { v: 2, l: 'Wed' },
  { v: 3, l: 'Thu' },
  { v: 4, l: 'Fri' },
  { v: 5, l: 'Sat' },
  { v: 6, l: 'Sun' },
];

const BLANK_DAY = { start: '', end: '', off: false };

const blankWeek = () => Array.from({ length: 7 }, () => ({ ...BLANK_DAY }));

// Compact human-readable summary of a user's weekly duty schedule for
// the expandable row (collapsed-details view). Groups consecutive days
// with the same window into ranges so Mon-Fri 09:00-18:00 doesn't eat
// five lines on a phone. Returns `null` when no duty window is set.
const summarizeDutyForRow = (user) => {
  const week = (() => {
    const raw = user?.duty_schedule;
    if (Array.isArray(raw) && raw.length === 7) {
      return raw.map((d) => ({
        start: (d?.start || '').trim(),
        end: (d?.end || '').trim(),
        off: !!d?.off,
      }));
    }
    const ds = (user?.duty_start || '').trim();
    const de = (user?.duty_end || '').trim();
    const dd = Array.isArray(user?.duty_days) ? user.duty_days.map(Number) : [];
    const od = Array.isArray(user?.off_duty_days) ? user.off_duty_days.map(Number) : [];
    const dutyDaysConfigured = dd.length > 0;
    return Array.from({ length: 7 }, (_, w) => {
      if (od.includes(w)) return { start: '', end: '', off: true };
      if (dutyDaysConfigured && !dd.includes(w)) return { start: '', end: '', off: true };
      return { start: ds, end: de, off: false };
    });
  })();
  const allOff = week.every((d) => d.off);
  if (allOff) return null;
  const hasAnyWindow = week.some((d) => !d.off && (d.start || d.end));
  if (!hasAnyWindow && week.every((d) => d.off || (!d.start && !d.end))) {
    // Everyone on duty 24/7 — nothing to show.
    return '24/7 access';
  }
  // Group consecutive days with the same (start, end, off) signature.
  const groups = [];
  const sig = (d) => (d.off ? 'OFF' : `${d.start || '00:00'}-${d.end || '24:00'}`);
  let cur = { s: sig(week[0]), from: 0, to: 0, day: week[0] };
  for (let i = 1; i < 7; i += 1) {
    const s = sig(week[i]);
    if (s === cur.s) {
      cur.to = i;
    } else {
      groups.push(cur);
      cur = { s, from: i, to: i, day: week[i] };
    }
  }
  groups.push(cur);
  const dayLabel = (v) => WEEKDAY_META[v]?.l || '';
  const range = (g) => (g.from === g.to ? dayLabel(g.from) : `${dayLabel(g.from)}-${dayLabel(g.to)}`);
  return groups
    .map((g) => {
      if (g.day.off) return `${range(g)} Off`;
      const win = g.s === 'OFF' ? '' : g.s;
      return win ? `${range(g)} ${win}` : `${range(g)} 24h`;
    })
    .join(' \u00b7 ');
};

// Client-side mirror of services/duty_schedule.derive_duty_schedule.
// Keeps legacy records working seamlessly in the UI — users whose
// records only have duty_start/duty_end/duty_days/off_duty_days see
// an already-populated 7-row grid instead of a blank one.
const deriveDutySchedule = (rec) => {
  if (!rec || typeof rec !== 'object') return blankWeek();
  const raw = rec.duty_schedule;
  if (Array.isArray(raw) && raw.length === 7) {
    return raw.map((e) => ({
      start: typeof e?.start === 'string' ? e.start : '',
      end: typeof e?.end === 'string' ? e.end : '',
      off: !!e?.off,
    }));
  }
  const ds = (rec.duty_start || '').trim();
  const de = (rec.duty_end || '').trim();
  const dd = Array.isArray(rec.duty_days) ? rec.duty_days.map(Number) : [];
  const od = Array.isArray(rec.off_duty_days) ? rec.off_duty_days.map(Number) : [];
  const dutyDaysConfigured = dd.length > 0;
  return Array.from({ length: 7 }, (_, w) => {
    if (od.includes(w)) return { start: '', end: '', off: true };
    if (dutyDaysConfigured && !dd.includes(w)) return { start: '', end: '', off: true };
    return { start: ds, end: de, off: false };
  });
};



const MODULE_LABELS = {
  products: 'Products',
  categories: 'Categories',
  orders: 'Orders',
  customers: 'Customers',
  drivers: 'Drivers',
  dispatch: 'Dispatch',
  marketing: 'Marketing / Inquiries',
  documents: 'PDF Documents',
  settings: 'Site settings',
  users: 'Admin users',
  pallets: 'Pallet Ledger',
  export: 'Export Departments',
};

const empty = {
  name: '', email: '', password: '',
  permissions: [], category_slugs: [], active: true,
  warehouse_roles: [], warehouse_scope: [],
  warehouse_features_denied: [],
  // Feb 2026 — inline order editor permission. Off by default.
  allow_order_inline_editing: false,
  // Feb 2026 — Floor page task-assignment permission. Off by default.
  allow_floor_task_assignment: false,
  // Feb 2026 — Pallet Returns floor tab permission. Off by default.
  allow_pallet_returns: false,
  allow_delete_loading_photos: false,
  allow_delete_received_documents: false,
  // Feb 2026 — Per-user Floor-Mode capability toggles. Off by default.
  allow_floor_receiving: false,
  allow_floor_putaway: false,
  allow_floor_loading: false,
  // Feb 2026 — ASN task assignment toggle. Off by default.
  allow_asn_task_assignment: false,
  // Feb 2026 — Day Close Reports toggle. Off by default.
  allow_day_close_reports: false,
  // Feb 2026 — Manual allocation toggle. Off by default.
  allow_manual_allocate: false,
  // Feb 2026 — Stock Check floor toggle. Off by default.
  allow_stock_check: false,
  // Feb 2026 — Floor Audit toggle. Off by default.
  allow_floor_audit: false,
  // Feb 2026 — Duty Hours + Off-duty Days access control (UTC+4).
  // Empty fields = no window, no gate. Both time fields must be
  // filled for the gate to activate. `duty_days` uses ISO ints —
  // 0=Monday ... 6=Sunday.
  duty_start: '',
  duty_end: '',
  duty_days: [],
  off_duty_days: [],
  // Feb 2026 — Per-weekday duty schedule. 7 entries indexed by
  // Python weekday (0=Mon..6=Sun). Each entry:
  // { start: 'HH:MM' | '', end: 'HH:MM' | '', off: bool }.
  // When present + non-empty, replaces the single duty_start/
  // duty_end + duty_days + off_duty_days triplet on save.
  duty_schedule: [
    { start: '', end: '', off: false },
    { start: '', end: '', off: false },
    { start: '', end: '', off: false },
    { start: '', end: '', off: false },
    { start: '', end: '', off: false },
    { start: '', end: '', off: false },
    { start: '', end: '', off: false },
  ],
  // Feb 2026 — Salesman scope. When non-empty the sub-admin only sees
  // Outstanding rows / customers / orders whose salesman matches ONE
  // of these tags. Empty = no restriction.
  assigned_salesmen: [],
};

/**
 * Warehouse roles — each is INDEPENDENT and can be combined with any
 * other. The `tier` value defines display order (lower = more privileged).
 * Descriptions surface next to each toggle inside the create/edit modal
 * AND next to the badges on the user row so admins understand what each
 * grant lets the sub-admin do.
 */
const WH_ROLE_DEFS = [
  {
    value: 'manager',
    label: 'Warehouse Manager',
    tier: 0,
    icon: Warehouse,
    color: 'emerald',
    description: 'Full access across every warehouse. Can create/edit/delete warehouses, approve transfers, run audits, and manage all placements. Always operates with global scope.',
  },
  {
    value: 'floor_incharge',
    label: 'Floor In-charge',
    tier: 1,
    icon: ClipboardList,
    color: 'blue',
    description: 'Manage placements, initiate stock transfers, spawn picking/put-away tasks, and confirm pick lists via barcode scan. Restricted to the warehouses assigned below.',
  },
  {
    value: 'picker',
    label: 'Picker',
    tier: 2,
    icon: ScanLine,
    color: 'amber',
    description: 'Read-only visibility over stock + can confirm assigned pick lists / put-away tasks by scanning pallet or item barcodes. Cannot create or delete placements.',
  },
  {
    value: 'viewer',
    label: 'Viewer',
    tier: 3,
    icon: Eye,
    color: 'neutral',
    description: 'Read-only access to warehouses, placements, stock reports and tasks. Cannot make any changes.',
  },
];

// Feb 2026 — Salesman multi-select picker used in the Edit Admin modal.
// Tiny, local to this page: shows a tag-cloud of each catalog name;
// clicking toggles membership. Case-insensitive dedupe keeps "ABDUL
// RAUF" and "abdul rauf" (same salesman, mixed casing in old rows)
// from double-rendering.
const SalesmanPicker = ({ catalog, value, onChange }) => {
  const selected = Array.isArray(value) ? value : [];
  const selectedNorm = new Set(
    selected.map((s) => String(s || '').trim().toLowerCase().replace(/\s+/g, ' ')),
  );
  const options = Array.isArray(catalog) ? catalog : [];
  const toggle = (tag) => {
    const norm = String(tag || '').trim().toLowerCase().replace(/\s+/g, ' ');
    if (!norm) return;
    const next = selectedNorm.has(norm)
      ? selected.filter(
          (s) => String(s || '').trim().toLowerCase().replace(/\s+/g, ' ') !== norm,
        )
      : [...selected, tag];
    onChange(next);
  };
  const clearAll = () => onChange([]);
  if (options.length === 0) {
    return (
      <div className="text-[12px] text-neutral-500 italic border border-dashed border-neutral-300 rounded px-3 py-2">
        No salesmen on file yet. Add a <span className="font-semibold">salesman</span> value to a customer first, then edit this admin to assign one.
      </div>
    );
  }
  return (
    <div
      data-testid="salesman-picker"
      className="border border-neutral-200 rounded-lg p-2 bg-neutral-50"
    >
      <div className="flex flex-wrap gap-1.5">
        {options.map((tag) => {
          const norm = String(tag || '').trim().toLowerCase().replace(/\s+/g, ' ');
          const active = selectedNorm.has(norm);
          return (
            <button
              key={tag}
              type="button"
              data-testid={`salesman-chip-${norm.replace(/\s+/g, '-')}`}
              onClick={() => toggle(tag)}
              className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition ${
                active
                  ? 'bg-emerald-600 text-white border-emerald-700 shadow-sm'
                  : 'bg-white text-neutral-700 border-neutral-300 hover:border-emerald-400'
              }`}
            >
              {tag}
            </button>
          );
        })}
      </div>
      <div className="flex items-center justify-between pt-2 mt-2 border-t border-neutral-200 text-[11px] text-neutral-500">
        <span>
          <span className="font-semibold text-neutral-700">{selected.length}</span> selected
          {selected.length === 0 ? ' — no restriction' : ''}
        </span>
        {selected.length > 0 && (
          <button
            type="button"
            data-testid="salesman-picker-clear"
            onClick={clearAll}
            className="text-rose-600 hover:underline font-medium"
          >
            Clear
          </button>
        )}
      </div>
    </div>
  );
};

const AdminUsers = () => {
  const { authHeaders } = useAdminAuth();
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [modules, setModules] = useState([]); // ordered list of module slugs
  const [actionsByModule, setActionsByModule] = useState({}); // { module: [{action,label}, …] }
  const [categories, setCategories] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseFeatures, setWarehouseFeatures] = useState([]); // [{id,label}]
  // Feb 2026 — Catalog of distinct salesman tags for the "Assigned
  // salesmen" dropdown. Loaded alongside users/perms/categories so
  // the modal has it ready on open.
  const [salesmenCatalog, setSalesmenCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | { ...form }
  const [busy, setBusy] = useState(false);
  // Which module sections are expanded in the create/edit modal. Defaults
  // to expanded for any module that already has any granted action.
  const [expanded, setExpanded] = useState({});
  // Feb 2026 — Which user list rows are expanded. Each row acts as its
  // own disclosure dropdown so the page works identically on mobile
  // (previously card-only) and desktop (previously wide table).
  const [expandedUsers, setExpandedUsers] = useState(() => new Set());
  const toggleUserExpanded = (id) => {
    setExpandedUsers((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  // Feb 2026 — "Copy mandate" picker. When set, the modal lets the
  // super admin pick one or more destination users and apply the
  // source user's full authorisation + duty surface in one call.
  // Identity fields (email, name, password) are NEVER touched.
  const [copyMandate, setCopyMandate] = useState(null); // { source, selected:Set, query:'', submitting:false }
  const openCopyMandate = (source) => {
    setCopyMandate({ source, selected: new Set(), query: '', submitting: false });
  };
  const closeCopyMandate = () => setCopyMandate(null);
  const toggleCopyTarget = (id) => {
    setCopyMandate((prev) => {
      if (!prev) return prev;
      const sel = new Set(prev.selected);
      if (sel.has(id)) sel.delete(id);
      else sel.add(id);
      return { ...prev, selected: sel };
    });
  };
  const confirmCopyMandate = async () => {
    if (!copyMandate || copyMandate.submitting) return;
    const sourceId = copyMandate.source?.id;
    const targetIds = Array.from(copyMandate.selected || []);
    if (!sourceId || targetIds.length === 0) return;
    setCopyMandate((prev) => prev ? { ...prev, submitting: true } : prev);
    try {
      const r = await axios.post(
        `${API}/admin/users/${sourceId}/copy-mandate`,
        { target_user_ids: targetIds },
        { headers: authHeaders },
      );
      const copied = r.data?.copied?.length || 0;
      const skipped = r.data?.skipped?.length || 0;
      toast({
        title: `Mandate copied to ${copied} user${copied === 1 ? '' : 's'}`,
        description: skipped > 0 ? `${skipped} skipped.` : undefined,
      });
      await load();
      setCopyMandate(null);
    } catch (err) {
      toast({
        title: 'Copy failed',
        description: err?.response?.data?.detail || err?.message || 'Please try again.',
      });
      setCopyMandate((prev) => prev ? { ...prev, submitting: false } : prev);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [u, p, c, w, wf, sm] = await Promise.all([
        axios.get(`${API}/admin/users`, { headers: authHeaders }),
        axios.get(`${API}/admin/users/permissions`, { headers: authHeaders }),
        axios.get(`${API}/admin/categories`, { headers: authHeaders }),
        axios.get(`${API}/admin/warehouses`, { headers: authHeaders }).catch(() => ({ data: { warehouses: [] } })),
        axios.get(`${API}/admin/warehouse-features/catalog`, { headers: authHeaders }).catch(() => ({ data: { features: [] } })),
        axios.get(`${API}/admin/salesmen`, { headers: authHeaders }).catch(() => ({ data: { salesmen: [] } })),
      ]);
      setUsers(u.data || []);
      setModules(p.data?.modules || []);
      setActionsByModule(p.data?.actions || {});
      setCategories(c.data || []);
      setWarehouses(w.data?.warehouses || []);
      setWarehouseFeatures(wf.data?.features || []);
      setSalesmenCatalog(sm.data?.salesmen || []);
    } catch (err) {
      toast({ title: 'Failed to load admin users', description: err?.response?.data?.detail || err?.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // Initial expanded state whenever we open the modal: expand any module
  // the user already has access to so the admin sees the action mix at
  // a glance.
  const computeInitialExpanded = (perms) => {
    const exp = {};
    for (const m of modules) {
      const hasModule = perms.includes(m);
      const hasAction = perms.some((p) => p.startsWith(`${m}.`));
      exp[m] = hasModule || hasAction;
    }
    return exp;
  };

  const openCreate = () => {
    setEditing({ ...empty });
    setExpanded({});
  };
  const openEdit = (u) => {
    const next = {
      id: u.id,
      name: u.name,
      email: u.email,
      password: '',
      permissions: [...u.permissions],
      category_slugs: [...(u.category_slugs || [])],
      // Prefer the new multi-role list; fall back to the legacy single field.
      warehouse_roles: [
        ...(Array.isArray(u.warehouse_roles) && u.warehouse_roles.length
          ? u.warehouse_roles
          : (u.warehouse_role ? [u.warehouse_role] : [])),
      ],
      warehouse_scope: [...(u.warehouse_scope || [])],
      warehouse_features_denied: [...(u.warehouse_features_denied || [])],
      active: u.active,
      allow_order_inline_editing: !!u.allow_order_inline_editing,
      allow_floor_task_assignment: !!u.allow_floor_task_assignment,
      allow_pallet_returns: !!u.allow_pallet_returns,
      allow_delete_loading_photos: !!u.allow_delete_loading_photos,
      allow_delete_received_documents: !!u.allow_delete_received_documents,
      allow_floor_receiving: !!u.allow_floor_receiving,
      allow_floor_putaway: !!u.allow_floor_putaway,
      allow_floor_loading: !!u.allow_floor_loading,
      allow_asn_task_assignment: !!u.allow_asn_task_assignment,
      allow_day_close_reports: !!u.allow_day_close_reports,
      allow_manual_allocate: !!u.allow_manual_allocate,
      allow_stock_check: !!u.allow_stock_check,
      allow_floor_audit: !!u.allow_floor_audit,
      // Feb 2026 — Duty hours + off-duty days.
      duty_start: u.duty_start || '',
      duty_end: u.duty_end || '',
      duty_days: Array.isArray(u.duty_days) ? [...u.duty_days] : [],
      off_duty_days: Array.isArray(u.off_duty_days) ? [...u.off_duty_days] : [],
      // Feb 2026 — Per-weekday schedule. Falls back to the legacy
      // triplet when the record doesn't have one yet.
      duty_schedule: deriveDutySchedule(u),
      // Feb 2026 — Salesman scope. Dedupe + canonicalise locally so
      // the picker surfaces the same spellings the backend persists.
      assigned_salesmen: Array.isArray(u.assigned_salesmen) ? [...u.assigned_salesmen] : [],
    };
    setEditing(next);
    setExpanded(computeInitialExpanded(next.permissions));
  };
  const close = () => setEditing(null);

  // ----- permission helpers (module + action) -----
  const hasModule = (perms, mod) => perms.includes(mod);
  const hasAction = (perms, mod, action) => perms.includes(`${mod}.${action}`);
  const hasAnyInModule = (perms, mod) =>
    hasModule(perms, mod) || perms.some((p) => p.startsWith(`${mod}.`));
  const hasLegacyBareOnly = (perms, mod) =>
    hasModule(perms, mod) && !perms.some((p) => p.startsWith(`${mod}.`));

  // Toggling the module-level checkbox.
  // - If currently has anything in this module → REMOVE everything.
  // - If empty → ADD bare module = read-only access (server interprets
  //   "bare module, no actions" as read-only).
  const toggleModule = (mod) => {
    setEditing((prev) => {
      if (!prev) return prev;
      const has = hasAnyInModule(prev.permissions, mod);
      const filtered = prev.permissions.filter(
        (p) => p !== mod && !p.startsWith(`${mod}.`),
      );
      return {
        ...prev,
        permissions: has ? filtered : [...filtered, mod],
      };
    });
    setExpanded((e) => ({ ...e, [mod]: !e[mod] }));
  };

  // Toggling a specific action checkbox.
  // - Once an action is added, the bare module slug becomes redundant
  //   (the action implicitly grants module visibility). We drop the
  //   bare slug to keep the stored list clean.
  // - If toggling off the last action, we keep at least the bare module
  //   slug so the admin doesn't lose module visibility unintentionally.
  const toggleAction = (mod, action) => {
    setEditing((prev) => {
      if (!prev) return prev;
      const slug = `${mod}.${action}`;
      const has = prev.permissions.includes(slug);
      let next = has
        ? prev.permissions.filter((p) => p !== slug)
        : [...prev.permissions, slug];
      // Drop bare module slug when at least one action is granted.
      const stillHasAction = next.some((p) => p.startsWith(`${mod}.`));
      if (stillHasAction) {
        next = next.filter((p) => p !== mod);
      } else if (!stillHasAction && !next.includes(mod)) {
        // Lost the last action — preserve module visibility (read-only).
        next = [...next, mod];
      }
      return { ...prev, permissions: next };
    });
  };

  // "Select all" toggle for a single module — grant every action.
  const selectAllInModule = (mod) => {
    setEditing((prev) => {
      if (!prev) return prev;
      const allSlugs = (actionsByModule[mod] || []).map(
        (a) => `${mod}.${a.action}`,
      );
      const filtered = prev.permissions.filter(
        (p) => p !== mod && !p.startsWith(`${mod}.`),
      );
      return { ...prev, permissions: [...filtered, ...allSlugs] };
    });
    setExpanded((e) => ({ ...e, [mod]: true }));
  };

  // Split a module's actions into [actions, fields] preserving order.
  // Default group is "actions" so modules that don't declare groups stay
  // backwards-compatible.
  const groupActions = (mod) => {
    const list = actionsByModule[mod] || [];
    // Feb 2026 — Backend catalog now emits three groups:
    //   'actions'      — module-standard action checkboxes (Create/Edit/Delete/…)
    //   'fields'       — per-field visibility flags (rendered as "Field visibility")
    //   'departments'  — export-workflow department roles (rendered as
    //                    "Departments" so super admin sees the five
    //                    per-stage edit permissions as their own group)
    // Anything that doesn't declare a group falls into `actions` so
    // legacy modules still render the way they always did.
    const actions = list.filter((a) => !a.group || a.group === 'actions');
    const fields = list.filter((a) => a.group === 'fields');
    const departments = list.filter((a) => a.group === 'departments');
    return { actions, fields, departments };
  };

  const toggleCategory = (slug) => {
    setEditing((prev) => {
      if (!prev) return prev;
      const has = prev.category_slugs.includes(slug);
      return {
        ...prev,
        category_slugs: has
          ? prev.category_slugs.filter((p) => p !== slug)
          : [...prev.category_slugs, slug],
      };
    });
  };

  const save = async () => {
    if (!editing) return;
    if (!editing.id && (!editing.email || !editing.password)) {
      toast({ title: 'Email and temporary password are required' });
      return;
    }
    if (!editing.id && editing.password.length < 8) {
      toast({ title: 'Password must be at least 8 characters' });
      return;
    }
    setBusy(true);
    try {
      if (editing.id) {
        const body = {
          name: editing.name,
          email: (editing.email || '').trim().toLowerCase(),
          permissions: editing.permissions,
          category_slugs: editing.category_slugs,
          warehouse_roles: editing.warehouse_roles || [],
          warehouse_scope: editing.warehouse_scope,
          warehouse_features_denied: editing.warehouse_features_denied || [],
          active: editing.active,
          allow_order_inline_editing: !!editing.allow_order_inline_editing,
          allow_floor_task_assignment: !!editing.allow_floor_task_assignment,
          allow_pallet_returns: !!editing.allow_pallet_returns,
          allow_delete_loading_photos: !!editing.allow_delete_loading_photos,
          allow_delete_received_documents: !!editing.allow_delete_received_documents,
          allow_floor_receiving: !!editing.allow_floor_receiving,
          allow_floor_putaway: !!editing.allow_floor_putaway,
          allow_floor_loading: !!editing.allow_floor_loading,
          allow_asn_task_assignment: !!editing.allow_asn_task_assignment,
          allow_day_close_reports: !!editing.allow_day_close_reports,
          allow_manual_allocate: !!editing.allow_manual_allocate,
          allow_stock_check: !!editing.allow_stock_check,
          allow_floor_audit: !!editing.allow_floor_audit,
          duty_start: editing.duty_start || '',
          duty_end: editing.duty_end || '',
          duty_days: Array.isArray(editing.duty_days) ? editing.duty_days : [],
          off_duty_days: Array.isArray(editing.off_duty_days) ? editing.off_duty_days : [],
          // Feb 2026 — Per-weekday schedule. The backend uses this
          // as the source of truth and back-fills the legacy
          // fields from it; legacy fields are kept in the payload
          // for defensive back-compat with any middleware that
          // still inspects them.
          duty_schedule: Array.isArray(editing.duty_schedule) && editing.duty_schedule.length === 7
            ? editing.duty_schedule.map((d) => ({
                start: (d?.start || '').trim(),
                end: (d?.end || '').trim(),
                off: !!d?.off,
              }))
            : [],
          assigned_salesmen: Array.isArray(editing.assigned_salesmen) ? editing.assigned_salesmen : [],
        };
        if (editing.password) body.password = editing.password;
        const res = await axios.put(`${API}/admin/users/${editing.id}`, body, { headers: authHeaders });
        // Optimistic in-place update so the list reflects changes without
        // a full reload flicker.
        setUsers((prev) => prev.map((x) => (x.id === editing.id ? res.data : x)));
        toast({ title: 'Admin user updated' });
      } else {
        const res = await axios.post(`${API}/admin/users`, editing, { headers: authHeaders });
        setUsers((prev) => [...prev, res.data]);
        toast({ title: 'Admin user created', description: `Temporary password sent. ${editing.email} must change it on first sign-in.` });
      }
      close();
      // Skip full reload — optimistic state updates above already reflect
      // the change instantly without a page refresh.
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || err?.message });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (u) => {
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!confirm(`Delete admin ${u.email}? They will lose access immediately.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${u.id}`, { headers: authHeaders });
      toast({ title: 'Admin deleted' });
      load();
    } catch (err) {
      toast({ title: 'Delete failed', description: err?.response?.data?.detail || err?.message });
    }
  };

  // Generates a fresh temp password server-side and shows it once so the
  // super-admin can hand it over. The sub-admin will be forced to change it
  // on their next sign-in (must_change_password=true on the record).
  const resetPassword = async (u) => {
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!confirm(`Generate a new temp password for ${u.email}?\nThey'll be forced to change it on next sign-in. Their current password stops working.`)) return;
    try {
      const { data } = await axios.post(`${API}/admin/users/${u.id}/reset-password`, {}, { headers: authHeaders });
      // eslint-disable-next-line no-restricted-globals, no-alert
      window.prompt(
        `${data.user_email} — new temp password (copy NOW, won't be shown again).\nThey'll be required to change it on first sign-in.`,
        data.temp_password,
      );
      toast({ title: 'Password reset', description: data.user_email });
    } catch (err) {
      toast({ title: 'Reset failed', description: err?.response?.data?.detail || err?.message });
    }
  };

  // ----- list-view permission summary chips -----
  const summarizePermissions = (perms) => {
    // Returns [{ mod, label, badge }] where badge ∈ 'Full'|'Read-only'|'<n> actions'.
    return modules
      .map((mod) => {
        const hasMod = perms.includes(mod);
        const actions = (actionsByModule[mod] || []).map((a) => `${mod}.${a.action}`);
        const granted = perms.filter((p) => actions.includes(p));
        const total = actions.length;
        if (!hasMod && granted.length === 0) return null;
        let badge = `${granted.length}/${total}`;
        if (hasMod && granted.length === 0) badge = 'Read-only';
        else if (granted.length === total) badge = 'Full';
        return { mod, label: MODULE_LABELS[mod] || mod, badge };
      })
      .filter(Boolean);
  };

  const moduleBadgeColor = (badge) => {
    if (badge === 'Full') return 'bg-emerald-50 border border-emerald-200 text-emerald-800';
    if (badge === 'Read-only') return 'bg-amber-50 border border-amber-200 text-amber-800';
    return 'bg-[#E7F3FF] border border-[#0078D4]/20 text-[#005A9E]';
  };

  const summarizedRows = useMemo(
    () => users.map((u) => ({ u, summary: summarizePermissions(u.permissions || []) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [users, modules, actionsByModule],
  );

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto px-5 lg:px-8 py-8">
        <div className="flex items-start justify-between mb-6 gap-3 flex-wrap">
          <div>
            <h1 className="font-display text-2xl font-bold text-[#0F172A]">Admin users</h1>
            <p className="text-neutral-500 mt-1 text-sm">
              Create sub-admins and authorize exactly what they can do — per module, per action.
              The original super admin (env-bound) always has full access and isn't shown here.
            </p>
          </div>
          <button
            type="button"
            onClick={openCreate}
            className="rounded-full bg-[#0F172A] text-white px-4 py-2 text-xs font-bold inline-flex items-center gap-1.5"
            data-testid="admin-user-create-btn"
          >
            <UserPlus className="w-4 h-4" /> New admin
          </button>
        </div>

        {loading ? (
          <div className="rounded-2xl bg-white border border-neutral-200 p-8 flex items-center gap-2 text-sm text-neutral-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : users.length === 0 ? (
          <div className="rounded-2xl bg-white border border-neutral-200 p-10 text-center">
            <ShieldCheck className="w-10 h-10 mx-auto text-neutral-300 mb-3" />
            <div className="text-sm text-neutral-500">No sub-admins yet — only the super admin can sign in.</div>
            <button
              type="button"
              onClick={openCreate}
              className="mt-4 rounded-full bg-[#0F172A] text-white px-4 py-2 text-xs font-bold inline-flex items-center gap-1.5"
            >
              <UserPlus className="w-4 h-4" /> Create the first sub-admin
            </button>
          </div>
        ) : (
          <>
            {/* Feb 2026 — Unified expandable list. One layout for mobile
                and desktop: tap/click a row header to disclose its full
                permission, warehouse, duty, and action details. Replaces
                the old mobile-cards + desktop-table split so every item
                feels native on a phone. */}
            <div
              className="rounded-2xl bg-white border border-neutral-200 overflow-hidden divide-y divide-neutral-100"
              data-testid="admin-users-list"
            >
              {summarizedRows.map(({ u, summary }) => {
                const isOpen = expandedUsers.has(u.id);
                const rolesList = Array.isArray(u.warehouse_roles) && u.warehouse_roles.length
                  ? u.warehouse_roles
                  : (u.warehouse_role ? [u.warehouse_role] : []);
                const whColorClass = {
                  emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
                  blue:    'bg-blue-50 border-blue-200 text-blue-800',
                  amber:   'bg-amber-50 border-amber-200 text-amber-800',
                  neutral: 'bg-neutral-100 border-neutral-200 text-neutral-700',
                  indigo:  'bg-indigo-50 border-indigo-200 text-indigo-800',
                };
                const dutyText = summarizeDutyForRow(u);
                return (
                  <div key={u.id} data-testid={`admin-user-row-${u.id}`}>
                    {/* Header — always visible. Tap toggles the body. */}
                    <button
                      type="button"
                      onClick={() => toggleUserExpanded(u.id)}
                      aria-expanded={isOpen}
                      className={`w-full flex items-center gap-3 text-left px-4 py-3 md:px-5 md:py-3.5 transition ${
                        isOpen ? 'bg-sky-50/40' : 'hover:bg-neutral-50'
                      }`}
                      data-testid={`admin-user-row-toggle-${u.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-[#0F172A] text-sm truncate">
                          {u.name || u.email}
                        </div>
                        <div className="text-[11px] text-neutral-500 truncate">{u.email}</div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            u.active
                              ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
                              : 'bg-neutral-100 border border-neutral-200 text-neutral-500'
                          }`}
                        >
                          {u.active ? 'Active' : 'Inactive'}
                        </span>
                        <span className="hidden sm:inline text-[10px] text-neutral-500 font-semibold">
                          {summary.length === 0
                            ? 'No access'
                            : `${summary.length} mod${summary.length === 1 ? '' : 's'}`}
                        </span>
                        <ChevronDown
                          className={`w-4 h-4 text-neutral-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                        />
                      </div>
                    </button>

                    {/* Expanded body — permissions, categories, warehouse,
                        duty summary, and action buttons. Mobile-first
                        layout: everything stacks and wraps. */}
                    {isOpen && (
                      <div
                        className="px-4 pb-4 md:px-5 md:pb-5 pt-1 space-y-3 bg-sky-50/20"
                        data-testid={`admin-user-row-body-${u.id}`}
                      >
                        <div>
                          <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-1">
                            Permissions
                          </div>
                          {summary.length === 0 ? (
                            <span className="text-xs text-amber-600 font-semibold">No access</span>
                          ) : (
                            <div className="flex gap-1 flex-wrap">
                              {summary.map(({ mod, label, badge }) => (
                                <span
                                  key={mod}
                                  className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${moduleBadgeColor(badge)}`}
                                  title={`${label}: ${badge}`}
                                >
                                  {label} \u00b7 {badge}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>

                        {hasAnyInModule(u.permissions, 'products') && (u.category_slugs || []).length > 0 && (
                          <div>
                            <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-1">
                              Category scope
                            </div>
                            <div className="flex gap-1 flex-wrap">
                              {(u.category_slugs || []).map((cs) => {
                                const cat = categories.find((c) => c.slug === cs);
                                return (
                                  <span
                                    key={cs}
                                    className="px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-[10px] font-bold"
                                  >
                                    {cat?.name || cs}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {rolesList.length > 0 && (
                          <div>
                            <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-1">
                              Warehouse
                            </div>
                            <div
                              className="flex gap-1 flex-wrap"
                              data-testid={`admin-user-wh-badges-${u.id}`}
                            >
                              {rolesList.map((r) => {
                                const def = WH_ROLE_DEFS.find((d) => d.value === r);
                                const Icon = def?.icon;
                                return (
                                  <span
                                    key={r}
                                    className={`px-2 py-0.5 rounded-full border text-[10px] font-bold inline-flex items-center gap-1 ${whColorClass[def?.color] || whColorClass.indigo}`}
                                    title={def?.description || r}
                                    data-testid={`admin-user-wh-badge-${u.id}-${r}`}
                                  >
                                    {Icon && <Icon className="w-3 h-3" />}
                                    {def?.label || r}
                                  </span>
                                );
                              })}
                              {!rolesList.includes('manager') && (u.warehouse_scope || []).length > 0 && (
                                (u.warehouse_scope || []).includes('*') ? (
                                  <span className="px-2 py-0.5 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-800 text-[10px] font-bold">
                                    All warehouses
                                  </span>
                                ) : (
                                  (u.warehouse_scope || []).map((wid) => {
                                    const w = warehouses.find((x) => x.id === wid);
                                    return (
                                      <span
                                        key={wid}
                                        className="px-2 py-0.5 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-800 text-[10px] font-bold"
                                      >
                                        {w?.name || wid}
                                      </span>
                                    );
                                  })
                                )
                              )}
                            </div>
                          </div>
                        )}

                        {dutyText && (
                          <div data-testid={`admin-user-row-duty-${u.id}`}>
                            <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-1">
                              Duty hours
                            </div>
                            <div className="text-xs text-neutral-700 leading-relaxed">{dutyText}</div>
                          </div>
                        )}

                        {u.must_change_password && (
                          <div className="text-[11px] text-amber-700 font-semibold bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
                            Must change password on next sign-in
                          </div>
                        )}

                        {/* Action buttons — stack to full width on mobile,
                            wrap inline on sm+. */}
                        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 pt-1">
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); openEdit(u); }}
                            className="inline-flex items-center justify-center gap-1.5 text-xs font-bold text-white bg-[#0078D4] hover:bg-[#005A9E] rounded-full px-3 py-2 sm:py-1.5"
                            data-testid={`admin-user-edit-${u.id}`}
                          >
                            <Edit3 className="w-3.5 h-3.5" /> Edit
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); openCopyMandate(u); }}
                            className="inline-flex items-center justify-center gap-1.5 text-xs font-bold text-sky-700 bg-sky-50 border border-sky-200 hover:bg-sky-100 rounded-full px-3 py-2 sm:py-1.5"
                            data-testid={`admin-user-copy-mandate-${u.id}`}
                            title="Copy this user's permissions, categories, warehouse scope, and duty hours to another user"
                          >
                            <Copy className="w-3.5 h-3.5" /> Copy mandate to{"\u2026"}
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); resetPassword(u); }}
                            className="inline-flex items-center justify-center gap-1.5 text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 hover:bg-amber-100 rounded-full px-3 py-2 sm:py-1.5"
                            data-testid={`admin-user-reset-pw-${u.id}`}
                          >
                            <KeyRound className="w-3.5 h-3.5" /> Reset password
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); remove(u); }}
                            className="inline-flex items-center justify-center gap-1.5 text-xs font-bold text-red-700 bg-red-50 border border-red-200 hover:bg-red-100 rounded-full px-3 py-2 sm:py-1.5"
                            data-testid={`admin-user-delete-${u.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" /> Delete
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Copy mandate picker — Feb 2026. Super-admin picks any number
          of destination users and applies the source user's full
          authorisation + duty surface in one call. Identity fields on
          the targets stay untouched. */}
      {copyMandate && (() => {
        const sourceId = copyMandate.source?.id;
        const q = (copyMandate.query || '').trim().toLowerCase();
        const eligible = users.filter((u) => {
          if (!u || u.id === sourceId) return false;
          if (!q) return true;
          return (
            (u.name || '').toLowerCase().includes(q) ||
            (u.email || '').toLowerCase().includes(q)
          );
        });
        const selectedCount = copyMandate.selected.size;
        return (
          <div
            className="fixed inset-0 bg-black/40 flex md:items-center md:justify-center z-50 md:p-4"
            onClick={copyMandate.submitting ? undefined : closeCopyMandate}
            data-testid="admin-user-copy-mandate-modal"
          >
            <div
              className="bg-white w-full h-full md:h-auto md:max-h-[85vh] md:max-w-xl md:rounded-2xl shadow-2xl flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 md:p-5 border-b border-neutral-100 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-sky-700">
                    Copy mandate
                  </div>
                  <div className="font-display text-base md:text-lg font-bold text-[#0F172A] truncate">
                    From {copyMandate.source?.name || copyMandate.source?.email}
                  </div>
                  <div className="text-[11px] text-neutral-500 leading-snug mt-0.5">
                    Permissions, category scope, warehouse role &amp; scope, and
                    duty hours will be overwritten on the chosen users.
                    Email, name, password, and active state stay the same.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={closeCopyMandate}
                  disabled={copyMandate.submitting}
                  className="text-neutral-400 hover:text-neutral-700 p-1 disabled:opacity-40 shrink-0"
                  data-testid="admin-user-copy-mandate-close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-3 md:p-4 border-b border-neutral-100">
                <div className="relative">
                  <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    autoFocus
                    placeholder="Search by name or email"
                    value={copyMandate.query}
                    onChange={(e) => setCopyMandate((prev) => prev
                      ? { ...prev, query: e.target.value }
                      : prev)}
                    className="w-full h-10 pl-9 pr-3 rounded-lg border border-neutral-300 text-sm focus:border-sky-500 focus:outline-none"
                    data-testid="admin-user-copy-mandate-search"
                  />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto">
                {eligible.length === 0 ? (
                  <div className="p-6 text-center text-sm text-neutral-500">
                    No matching users.
                  </div>
                ) : (
                  <ul className="divide-y divide-neutral-100">
                    {eligible.map((u) => {
                      const checked = copyMandate.selected.has(u.id);
                      return (
                        <li key={u.id}>
                          <label
                            className={`flex items-center gap-3 px-4 py-3 cursor-pointer ${
                              checked ? 'bg-sky-50/60' : 'hover:bg-neutral-50'
                            }`}
                            data-testid={`admin-user-copy-mandate-target-${u.id}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleCopyTarget(u.id)}
                              className="w-4 h-4 accent-sky-600"
                            />
                            <div className="min-w-0 flex-1">
                              <div className="font-semibold text-sm text-[#0F172A] truncate">
                                {u.name || u.email}
                              </div>
                              <div className="text-[11px] text-neutral-500 truncate">
                                {u.email}
                              </div>
                            </div>
                            {!u.active && (
                              <span className="text-[10px] font-bold text-neutral-500 bg-neutral-100 border border-neutral-200 rounded-full px-2 py-0.5">
                                Inactive
                              </span>
                            )}
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              <div className="p-4 border-t border-neutral-100 bg-white flex items-center justify-between gap-2">
                <div className="text-xs text-neutral-500">
                  <span className="font-bold text-[#0F172A]">
                    {selectedCount}
                  </span>{' '}
                  user{selectedCount === 1 ? '' : 's'} selected
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={closeCopyMandate}
                    disabled={copyMandate.submitting}
                    className="text-xs font-semibold text-neutral-600 hover:text-neutral-900 px-3 py-2 rounded-full border border-neutral-300 hover:bg-neutral-50 disabled:opacity-40"
                    data-testid="admin-user-copy-mandate-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={confirmCopyMandate}
                    disabled={selectedCount === 0 || copyMandate.submitting}
                    className="text-xs font-bold text-white px-4 py-2 rounded-full bg-sky-600 hover:bg-sky-700 disabled:opacity-40 inline-flex items-center gap-1.5"
                    data-testid="admin-user-copy-mandate-confirm"
                  >
                    {copyMandate.submitting
                      ? (<><Loader2 className="w-3.5 h-3.5 animate-spin" /> Copying{"\u2026"}</>)
                      : selectedCount === 0
                        ? 'Pick users to copy to'
                        : `Copy to ${selectedCount} user${selectedCount === 1 ? '' : 's'}`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Create / Edit modal — full-screen on mobile, centered card on desktop */}
      {editing && (
        <div
          className="fixed inset-0 bg-black/40 flex md:items-center md:justify-center z-50 md:p-4"
          onClick={close}
          data-testid="admin-user-modal"
        >
          <div
            className="bg-white w-full md:max-w-2xl md:max-h-[92vh] md:rounded-2xl md:overflow-y-auto shadow-2xl h-full md:h-auto overflow-y-auto pb-28 md:pb-0"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-neutral-100 sticky top-0 bg-white z-10">
              <h2 className="font-display text-lg font-bold text-[#0F172A]">
                {editing.id ? 'Edit admin user' : 'New admin user'}
              </h2>
              <button type="button" onClick={close} className="text-neutral-500 hover:text-neutral-900">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label htmlFor="admin-user-name" className="block text-[10px] font-bold tracking-wider text-neutral-500 uppercase mb-1">Full name</label>
                <input
                  id="admin-user-name"
                  type="text"
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  placeholder="e.g. Sales Manager"
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0078D4]/40"
                  data-testid="admin-user-modal-name"
                />
              </div>

              <div>
                <label htmlFor="admin-user-email" className="block text-[10px] font-bold tracking-wider text-neutral-500 uppercase mb-1">Email (sign-in)</label>
                <input
                  id="admin-user-email"
                  type="email"
                  value={editing.email}
                  onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                  placeholder="manager@mrpco.net"
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0078D4]/40"
                  data-testid="admin-user-modal-email"
                />
                {editing.id && (
                  <p className="text-[10px] text-amber-600 mt-1">
                    Changing the email changes this user's sign-in. Their active session will be invalidated — they must sign in again with the new email.
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="admin-user-pwd" className="block text-[10px] font-bold tracking-wider text-neutral-500 uppercase mb-1">
                  {editing.id ? 'Reset password (optional)' : 'Temporary password'}
                </label>
                <input
                  id="admin-user-pwd"
                  type="text"
                  value={editing.password}
                  onChange={(e) => setEditing({ ...editing, password: e.target.value })}
                  placeholder={editing.id ? 'Leave blank to keep current' : 'Min 8 characters'}
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0078D4]/40"
                  data-testid="admin-user-modal-password"
                />
                <p className="text-[10px] text-neutral-400 mt-1">User will be forced to change this on first sign-in.</p>
              </div>

              <div>
                <div className="flex items-baseline justify-between mb-2">
                  <div className="block text-[10px] font-bold tracking-wider text-neutral-500 uppercase">
                    Permissions
                  </div>
                  <span className="text-[10px] text-neutral-400 italic">
                    Tick module → read-only. Expand to grant specific actions.
                  </span>
                </div>
                <div className="space-y-2" data-testid="admin-user-modal-perms">
                  {modules.map((mod) => {
                    const actions = actionsByModule[mod] || [];
                    const moduleOn = hasAnyInModule(editing.permissions, mod);
                    const isExpanded = !!expanded[mod];
                    const grantedCount = editing.permissions.filter((p) =>
                      p.startsWith(`${mod}.`),
                    ).length;
                    const isLegacy = !!editing.id && hasLegacyBareOnly(editing.permissions, mod);
                    return (
                      <div
                        key={mod}
                        className={`rounded-lg border ${moduleOn ? 'border-[#0078D4]/50 bg-[#F4FAFF]' : 'border-neutral-200 bg-white'}`}
                      >
                        <div className="flex items-center gap-2 px-3 py-2.5">
                          <input
                            type="checkbox"
                            checked={moduleOn}
                            onChange={() => toggleModule(mod)}
                            className="w-4 h-4 accent-[#0078D4]"
                            data-testid={`admin-user-mod-${mod}`}
                          />
                          <button
                            type="button"
                            onClick={() => setExpanded((e) => ({ ...e, [mod]: !e[mod] }))}
                            className="flex-1 flex items-center gap-2 text-left"
                            data-testid={`admin-user-mod-expand-${mod}`}
                          >
                            <span className="text-sm font-semibold text-[#0F172A]">{MODULE_LABELS[mod] || mod}</span>
                            {moduleOn && (
                              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-white border border-neutral-300 text-neutral-600">
                                {grantedCount === 0
                                  ? (isLegacy ? 'Full (legacy)' : 'Read-only')
                                  : (grantedCount === actions.length ? 'Full' : `${grantedCount}/${actions.length}`)}
                              </span>
                            )}
                            <span className="ml-auto text-neutral-400">
                              {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                            </span>
                          </button>
                          {moduleOn && actions.length > 0 && (
                            <button
                              type="button"
                              onClick={() => selectAllInModule(mod)}
                              className="text-[10px] font-bold text-[#0078D4] hover:text-[#005A9E] px-2 py-1 rounded-md hover:bg-white"
                              data-testid={`admin-user-mod-all-${mod}`}
                            >
                              All
                            </button>
                          )}
                        </div>
                        {isExpanded && moduleOn && actions.length > 0 && (() => {
                          const { actions: act, fields: fld, departments: dep } = groupActions(mod);
                          return (
                            <div className="px-3 pb-3 pt-1 border-t border-[#0078D4]/10 space-y-3">
                              {isLegacy && (
                                <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
                                  Legacy access — full module. Tick any action below to switch to granular.
                                </div>
                              )}
                              {act.length > 0 && (
                                <div>
                                  <div className="text-[10px] font-bold tracking-wider text-neutral-400 uppercase mb-1.5 pl-1">
                                    Actions
                                  </div>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                                    {act.map((a) => (
                                      <label
                                        key={a.action}
                                        className="flex items-center gap-2 px-2 py-1.5 rounded-md text-xs hover:bg-white cursor-pointer"
                                      >
                                        <input
                                          type="checkbox"
                                          checked={hasAction(editing.permissions, mod, a.action)}
                                          onChange={() => toggleAction(mod, a.action)}
                                          className="w-3.5 h-3.5 accent-[#0078D4]"
                                          data-testid={`admin-user-act-${mod}-${a.action}`}
                                        />
                                        <span className="text-neutral-700">{a.label}</span>
                                      </label>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {dep.length > 0 && (
                                <div data-testid={`admin-user-dep-group-${mod}`}>
                                  <div className="text-[10px] font-bold tracking-wider text-neutral-400 uppercase mb-1.5 pl-1 flex items-center gap-2">
                                    Departments
                                    <span className="text-[9px] font-normal italic text-neutral-400 normal-case">
                                      one checkbox per department — grants edit access to that stage only
                                    </span>
                                  </div>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                                    {dep.map((a) => (
                                      <label
                                        key={a.action}
                                        className="flex items-center gap-2 px-2 py-1.5 rounded-md text-xs hover:bg-white cursor-pointer border border-transparent hover:border-neutral-200"
                                      >
                                        <input
                                          type="checkbox"
                                          checked={hasAction(editing.permissions, mod, a.action)}
                                          onChange={() => toggleAction(mod, a.action)}
                                          className="w-3.5 h-3.5 accent-[#0078D4]"
                                          data-testid={`admin-user-act-${mod}-${a.action}`}
                                        />
                                        <span className="text-neutral-700">{a.label}</span>
                                      </label>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {fld.length > 0 && (
                                <div>
                                  <div className="text-[10px] font-bold tracking-wider text-neutral-400 uppercase mb-1.5 pl-1 flex items-center gap-2">
                                    Field visibility
                                    <span className="text-[9px] font-normal italic text-neutral-400 normal-case">
                                      tick to show in list & edit form
                                    </span>
                                  </div>
                                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                                    {fld.map((a) => (
                                      <label
                                        key={a.action}
                                        className="flex items-center gap-2 px-2 py-1.5 rounded-md text-xs hover:bg-white cursor-pointer border border-transparent hover:border-neutral-200"
                                      >
                                        <input
                                          type="checkbox"
                                          checked={hasAction(editing.permissions, mod, a.action)}
                                          onChange={() => toggleAction(mod, a.action)}
                                          className="w-3.5 h-3.5 accent-[#0078D4]"
                                          data-testid={`admin-user-act-${mod}-${a.action}`}
                                        />
                                        <span className="text-neutral-700">{a.label}</span>
                                      </label>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Category scope — only relevant when the user has products access */}
              {hasAnyInModule(editing.permissions, 'products') && (
                <div data-testid="admin-user-modal-cats">
                  <div className="block text-[10px] font-bold tracking-wider text-neutral-500 uppercase mb-2">
                    Allowed product divisions
                  </div>
                  <p className="text-[11px] text-neutral-500 mb-2 leading-snug">
                    Restrict this admin to specific product divisions.
                    <span className="font-semibold"> Leave empty = access all categories.</span>
                  </p>
                  {categories.length === 0 ? (
                    <div className="text-xs text-neutral-400 italic">No divisions defined yet.</div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      {categories.map((c) => (
                        <label key={c.slug} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-neutral-200 hover:bg-neutral-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editing.category_slugs.includes(c.slug)}
                            onChange={() => toggleCategory(c.slug)}
                            className="w-4 h-4 accent-[#0078D4]"
                            data-testid={`admin-user-cat-${c.slug}`}
                          />
                          <span className="text-sm text-[#0F172A] truncate">{c.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Feb 2026 — Salesman scope. Independent of permissions:
                  pick one or more SMAN tags; when non-empty the sub-admin
                  only sees Outstanding Report rows tied to those salesmen.
                  Empty = no restriction. (Customers / Orders are NOT scoped
                  here per the Feb 2026 directive — Outstanding only.) */}
              <div data-testid="admin-user-modal-salesmen">
                <div className="block text-[10px] font-bold tracking-wider text-neutral-500 uppercase mb-2">
                  Assigned salesmen
                </div>
                <p className="text-[11px] text-neutral-500 mb-2 leading-snug">
                  Limit this admin to <span className="font-semibold">Outstanding Report</span> rows tagged with the picked salesman(s).{' '}
                  <span className="font-semibold">Leave empty = sees everything their permissions allow.</span>{' '}
                  Customers &amp; Orders pages are not restricted. Super admin is never restricted.
                </p>
                <SalesmanPicker
                  catalog={salesmenCatalog}
                  value={editing.assigned_salesmen}
                  onChange={(next) =>
                    setEditing((prev) => ({ ...prev, assigned_salesmen: next }))
                  }
                />
              </div>

              <div data-testid="admin-user-modal-warehouse">
                <div className="block text-[10px] font-bold tracking-wider text-neutral-500 uppercase mb-2">
                  Warehouse roles
                </div>
                <p className="text-[11px] text-neutral-500 mb-3 leading-snug">
                  Grant one or more warehouse-module roles. Roles are independent — combine them freely (e.g. a Floor In-charge who is also a Picker gets both capabilities). Adding <span className="font-semibold">Warehouse Manager</span> forces global scope.
                </p>

                <div className="space-y-2" data-testid="admin-user-modal-wh-roles">
                  {WH_ROLE_DEFS.map((def) => {
                    const active = (editing.warehouse_roles || []).includes(def.value);
                    const Icon = def.icon;
                    const ringColor = {
                      emerald: 'ring-emerald-300 bg-emerald-50/60',
                      blue:    'ring-blue-300 bg-blue-50/60',
                      amber:   'ring-amber-300 bg-amber-50/60',
                      neutral: 'ring-neutral-300 bg-neutral-50/60',
                    }[def.color];
                    const iconBg = {
                      emerald: 'bg-emerald-500 text-white',
                      blue:    'bg-blue-500 text-white',
                      amber:   'bg-amber-500 text-white',
                      neutral: 'bg-neutral-500 text-white',
                    }[def.color];
                    return (
                      <div
                        key={def.value}
                        className={`flex items-start gap-3 p-3 rounded-xl border transition-all ${active ? `border-transparent ring-2 ${ringColor}` : 'border-neutral-200 hover:border-neutral-300'}`}
                        data-testid={`admin-user-modal-wh-role-${def.value}`}
                      >
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${active ? iconBg : 'bg-neutral-100 text-neutral-500'}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-3">
                            <div className="text-sm font-bold text-[#0F172A]">{def.label}</div>
                            <Switch
                              checked={active}
                              onCheckedChange={(next) => {
                                setEditing((prev) => {
                                  const cur = new Set(prev.warehouse_roles || []);
                                  if (next) cur.add(def.value);
                                  else cur.delete(def.value);
                                  const order = ['manager', 'floor_incharge', 'picker', 'viewer'];
                                  const roles = order.filter((r) => cur.has(r));
                                  // Manager present → force global scope; else keep prev.
                                  const scope = roles.includes('manager')
                                    ? ['*']
                                    : (prev.warehouse_scope && prev.warehouse_scope.length ? prev.warehouse_scope : []);
                                  return { ...prev, warehouse_roles: roles, warehouse_scope: scope };
                                });
                              }}
                              data-testid={`admin-user-modal-wh-toggle-${def.value}`}
                            />
                          </div>
                          <p className="text-[11px] text-neutral-600 leading-snug mt-0.5">{def.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {(editing.warehouse_roles || []).length > 0 && !(editing.warehouse_roles || []).includes('manager') && (
                  <div className="mt-4">
                    <div className="text-[10px] font-bold tracking-wider text-neutral-500 uppercase mb-1.5">
                      Warehouse scope
                    </div>
                    <p className="text-[11px] text-neutral-500 mb-2 leading-snug">
                      Restrict the roles above to specific warehouses.
                      <span className="font-semibold"> Tick &quot;All warehouses&quot; for global access.</span>
                    </p>
                    <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-neutral-200 hover:bg-neutral-50 cursor-pointer mb-2">
                      <input
                        type="checkbox"
                        checked={(editing.warehouse_scope || []).includes('*')}
                        onChange={(e) => {
                          setEditing((prev) => ({
                            ...prev,
                            warehouse_scope: e.target.checked ? ['*'] : [],
                          }));
                        }}
                        className="w-4 h-4 accent-[#0078D4]"
                        data-testid="admin-user-modal-wh-scope-all"
                      />
                      <span className="text-sm font-semibold text-[#0F172A]">All warehouses (global)</span>
                    </label>
                    {!(editing.warehouse_scope || []).includes('*') && (
                      warehouses.length === 0 ? (
                        <div className="text-xs text-neutral-400 italic">No warehouses defined yet.</div>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {warehouses.map((w) => (
                            <label key={w.id} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-neutral-200 hover:bg-neutral-50 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={(editing.warehouse_scope || []).includes(w.id)}
                                onChange={() => {
                                  setEditing((prev) => {
                                    const cur = prev.warehouse_scope || [];
                                    const has = cur.includes(w.id);
                                    return {
                                      ...prev,
                                      warehouse_scope: has ? cur.filter((x) => x !== w.id) : [...cur.filter((x) => x !== '*'), w.id],
                                    };
                                  });
                                }}
                                className="w-4 h-4 accent-[#0078D4]"
                                data-testid={`admin-user-modal-wh-${w.id}`}
                              />
                              <span className="text-sm text-[#0F172A] truncate">{w.name}</span>
                            </label>
                          ))}
                        </div>
                      )
                    )}
                  </div>
                )}
                {(editing.warehouse_roles || []).includes('manager') && (
                  <div className="mt-3 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1.5">
                    Warehouse Manager is on — this user has global scope across every warehouse.
                  </div>
                )}
              </div>

              {/* Warehouse Access — per-user feature toggles.  Every id in
                  `warehouse_features_denied` hides the matching tab from this
                  user's Warehouse portal on their next request. Super admin
                  always sees every feature (server-side bypass). */}
              <div data-testid="admin-user-modal-warehouse-access">
                <div className="block text-[10px] font-bold tracking-wider text-neutral-500 uppercase mb-2">
                  Warehouse Access
                </div>
                <p className="text-[11px] text-neutral-500 mb-3 leading-snug">
                  Turn individual warehouse functions on or off for this user. When a function is off, the tab is hidden from their view on next sign-in. Warehouse rename remains restricted to the super admin regardless of toggles.
                </p>
                {warehouseFeatures.length === 0 ? (
                  <div className="text-xs text-neutral-400 italic">No warehouse features registered.</div>
                ) : (
                  <div className="space-y-2" data-testid="admin-user-modal-wh-features">
                    {warehouseFeatures.map((f) => {
                      const denied = (editing.warehouse_features_denied || []).includes(f.id);
                      const enabled = !denied;
                      return (
                        <div
                          key={f.id}
                          className={`flex items-center gap-3 p-3 rounded-xl border transition ${enabled ? 'border-neutral-200 bg-white' : 'border-red-200 bg-red-50/60'}`}
                          data-testid={`admin-user-modal-wh-feature-${f.id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-[#0F172A] truncate">{f.label}</div>
                            <div className="text-[11px] text-neutral-500">
                              Feature id · <code>{f.id}</code>
                              <span className={`ml-2 px-1.5 py-0.5 rounded-full text-[9px] font-bold ${enabled ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`} data-testid={`admin-user-modal-wh-feature-state-${f.id}`}>
                                {enabled ? 'ALLOWED' : 'HIDDEN'}
                              </span>
                            </div>
                          </div>
                          <Switch
                            checked={enabled}
                            onCheckedChange={(next) => {
                              setEditing((prev) => {
                                const cur = new Set(prev.warehouse_features_denied || []);
                                if (next) cur.delete(f.id); else cur.add(f.id);
                                return { ...prev, warehouse_features_denied: [...cur] };
                              });
                            }}
                            data-testid={`admin-user-modal-wh-feature-toggle-${f.id}`}
                            aria-label={`${enabled ? 'Hide' : 'Show'} ${f.label} for this user`}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-neutral-200 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editing.active}
                  onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                  className="w-4 h-4 accent-[#0078D4]"
                  data-testid="admin-user-modal-active"
                />
                <span className="text-sm text-[#0F172A]">Active — uncheck to revoke access without deleting</span>
              </label>

              {/* Feb 2026 — Per-weekday duty schedule. 7-row grid.
                  Each row: weekday, Off toggle, start, end. Blank
                  start/end on an on-duty day = "no window" (always-on
                  that day). Off locks the whole day. */}
              <div
                className="rounded-xl border border-sky-200 bg-sky-50/50 p-3 space-y-3"
                data-testid="admin-user-modal-duty-section"
              >
                <div className="flex items-baseline justify-between gap-2 flex-wrap">
                  <div className="text-xs font-bold uppercase tracking-wider text-sky-800">
                    Weekly duty schedule
                  </div>
                  <div className="text-[10px] font-normal text-neutral-500">
                    Mon &rarr; Sun &middot; local time (UTC+4)
                  </div>
                </div>
                <div className="rounded-lg border border-sky-200 overflow-hidden bg-white">
                  {/* Desktop-only header. On mobile each row self-labels so a
                      separate header would just steal vertical space. */}
                  <div className="hidden sm:grid grid-cols-[80px_60px_1fr_1fr] text-[10px] font-semibold uppercase tracking-wide text-neutral-500 bg-sky-50/70 border-b border-sky-100">
                    <div className="px-2 py-1.5">Day</div>
                    <div className="px-2 py-1.5 text-center">Off</div>
                    <div className="px-2 py-1.5">Start</div>
                    <div className="px-2 py-1.5">End</div>
                  </div>
                  {WEEKDAY_META.map((d) => {
                    const dayRow = (Array.isArray(editing.duty_schedule) && editing.duty_schedule[d.v])
                      || { ...BLANK_DAY };
                    const isOff = !!dayRow.off;
                    const updateDay = (patch) => {
                      const next = Array.isArray(editing.duty_schedule) && editing.duty_schedule.length === 7
                        ? editing.duty_schedule.map((e) => ({ ...e }))
                        : blankWeek();
                      next[d.v] = { ...next[d.v], ...patch };
                      setEditing({ ...editing, duty_schedule: next });
                    };
                    return (
                      <div
                        key={d.v}
                        className={`border-t border-neutral-100 ${
                          isOff ? 'bg-amber-50/40' : 'bg-white'
                        }`}
                        data-testid={`admin-user-modal-duty-row-${d.v}`}
                      >
                        {/* Mobile layout (<sm): day name + Off toggle on
                            top line, then Start/End time inputs on a
                            second line as a 2-col grid. Larger touch
                            targets (h-10). */}
                        <div className="sm:hidden p-2.5 space-y-2">
                          <label className="flex items-center justify-between gap-2 cursor-pointer">
                            <span className="text-sm font-semibold text-neutral-800">{d.l}</span>
                            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-amber-700">
                              <input
                                type="checkbox"
                                checked={isOff}
                                onChange={(e) => updateDay({
                                  off: e.target.checked,
                                  ...(e.target.checked ? { start: '', end: '' } : {}),
                                })}
                                className="w-4 h-4 accent-amber-500"
                              />
                              Off
                            </span>
                          </label>
                          <div className="grid grid-cols-2 gap-2">
                            <label className="block">
                              <span className="block text-[10px] font-semibold uppercase tracking-wide text-neutral-500 mb-0.5">Start</span>
                              <input
                                type="time"
                                value={dayRow.start || ''}
                                disabled={isOff}
                                onChange={(e) => updateDay({ start: e.target.value })}
                                className={`w-full h-10 px-2 rounded-md border text-sm tabular-nums focus:border-sky-500 focus:outline-none ${
                                  isOff ? 'bg-neutral-100 text-neutral-400 border-neutral-200' : 'border-neutral-300'
                                }`}
                              />
                            </label>
                            <label className="block">
                              <span className="block text-[10px] font-semibold uppercase tracking-wide text-neutral-500 mb-0.5">End</span>
                              <input
                                type="time"
                                value={dayRow.end || ''}
                                disabled={isOff}
                                onChange={(e) => updateDay({ end: e.target.value })}
                                className={`w-full h-10 px-2 rounded-md border text-sm tabular-nums focus:border-sky-500 focus:outline-none ${
                                  isOff ? 'bg-neutral-100 text-neutral-400 border-neutral-200' : 'border-neutral-300'
                                }`}
                              />
                            </label>
                          </div>
                        </div>

                        {/* Desktop layout (sm+): keep the 4-col grid. */}
                        <div className="hidden sm:grid grid-cols-[80px_60px_1fr_1fr] items-center">
                          <div className="px-2 py-1.5 text-xs font-semibold text-neutral-700">
                            {d.l}
                          </div>
                          <div className="px-2 py-1.5 flex justify-center">
                            <input
                              type="checkbox"
                              checked={isOff}
                              onChange={(e) => updateDay({
                                off: e.target.checked,
                                ...(e.target.checked ? { start: '', end: '' } : {}),
                              })}
                              className="w-4 h-4 accent-amber-500 cursor-pointer"
                              data-testid={`admin-user-modal-duty-off-${d.v}`}
                            />
                          </div>
                          <div className="px-2 py-1.5">
                            <input
                              type="time"
                              value={dayRow.start || ''}
                              disabled={isOff}
                              onChange={(e) => updateDay({ start: e.target.value })}
                              className={`w-full h-8 px-2 rounded-md border text-xs tabular-nums focus:border-sky-500 focus:outline-none ${
                                isOff ? 'bg-neutral-100 text-neutral-400 border-neutral-200 cursor-not-allowed' : 'border-neutral-300'
                              }`}
                              data-testid={`admin-user-modal-duty-start-${d.v}`}
                            />
                          </div>
                          <div className="px-2 py-1.5">
                            <input
                              type="time"
                              value={dayRow.end || ''}
                              disabled={isOff}
                              onChange={(e) => updateDay({ end: e.target.value })}
                              className={`w-full h-8 px-2 rounded-md border text-xs tabular-nums focus:border-sky-500 focus:outline-none ${
                                isOff ? 'bg-neutral-100 text-neutral-400 border-neutral-200 cursor-not-allowed' : 'border-neutral-300'
                              }`}
                              data-testid={`admin-user-modal-duty-end-${d.v}`}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="text-[10px] text-neutral-500 leading-relaxed">
                  Mark a day as <span className="font-semibold text-amber-700">Off</span> to lock the whole day. Leave both times blank on an on-duty day for 24/7 access. Super admin ({(editing.email || '').toLowerCase() || 'super'}) is always exempt.
                </div>
              </div>
              {/* Feb 2026 — inline order editor toggle. When ON, this sub-admin
                  can click line items on the Admin Orders page to change
                  qty/price, remove items, or add new ones. Edits require a
                  branch/customer confirmation before the order resumes. */}
              <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-amber-200 bg-amber-50/40 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!editing.allow_order_inline_editing}
                  onChange={(e) => setEditing({ ...editing, allow_order_inline_editing: e.target.checked })}
                  className="w-4 h-4 accent-[#B45309]"
                  data-testid="admin-user-modal-allow-order-inline-editing"
                />
                <span className="text-sm text-[#0F172A]">
                  Allow order inline editing
                  <span className="block text-[11px] text-neutral-500 mt-0.5">
                    Change quantities, remove items, edit prices, or add new items directly on the order card. Every edit requires branch/customer confirmation before the order resumes.
                  </span>
                </span>
              </label>
              {/* Feb 2026 — Floor page task-assignment toggle. When ON,
                  this sub-admin can hand out picking / loading /
                  put-away tasks straight from the Floor page. Grants
                  the Floor In-charge (or any trusted lead) the same
                  assignment power as a manager on that surface,
                  without granting the wider `manager` warehouse role. */}
              <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-blue-200 bg-blue-50/40 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!editing.allow_floor_task_assignment}
                  onChange={(e) => setEditing({ ...editing, allow_floor_task_assignment: e.target.checked })}
                  className="w-4 h-4 accent-[#0078D4]"
                  data-testid="admin-user-modal-allow-floor-task-assignment"
                />
                <span className="text-sm text-[#0F172A]">
                  Allow Floor-page task assignment
                  <span className="block text-[11px] text-neutral-500 mt-0.5">
                    Assign users to picking, loading and put-away tasks directly from the Floor page. Independent of the sidebar &ldquo;Assign WMS tasks per order&rdquo; permission.
                  </span>
                </span>
              </label>

              {/* Feb 2026 — Pallet Returns floor toggle. When ON, this
                  user sees a "Pallet Returns" tab on the Floor page
                  where they can confirm branch-initiated physical
                  returns as they arrive at the dock. Independent of
                  the wider `pallets.manage` permission. */}
              <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-indigo-200 bg-indigo-50/40 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!editing.allow_pallet_returns}
                  onChange={(e) => setEditing({ ...editing, allow_pallet_returns: e.target.checked })}
                  className="w-4 h-4 accent-[#4F46E5]"
                  data-testid="admin-user-modal-allow-pallet-returns"
                />
                <span className="text-sm text-[#0F172A]">
                  Allow Pallet Returns at dock
                  <span className="block text-[11px] text-neutral-500 mt-0.5">
                    Surface the &ldquo;Pallet Returns&rdquo; tab on the Floor page so this user can receive branch pallet returns and credit warehouse inventory. Independent of the full Pallet Ledger admin module.
                  </span>
                </span>
              </label>

              {/* Feb 2026 — Stock Check floor toggle. When ON, this
                  user sees a "Stock Check" tab on the Floor page with
                  a search bar (Pallet IDs, SKUs, Location IDs) and
                  live on-hand / available / allocated cards. Read-only
                  — no inventory mutations. */}
              <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-teal-200 bg-teal-50/40 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!editing.allow_stock_check}
                  onChange={(e) => setEditing({ ...editing, allow_stock_check: e.target.checked })}
                  className="w-4 h-4 accent-teal-600"
                  data-testid="admin-user-modal-allow-stock-check"
                />
                <span className="text-sm text-[#0F172A]">
                  Allow Stock Check on Floor
                  <span className="block text-[11px] text-neutral-500 mt-0.5">
                    Surface a &ldquo;Stock Check&rdquo; search on the Floor page — scans Pallet IDs / SKUs / Location IDs and shows on-hand, allocated and batch info. Read-only lookup, no inventory changes.
                  </span>
                </span>
              </label>

              {/* Feb 2026 — Floor Audit toggle. When ON, this user
                  sees an "Audit" tab on the Floor page: search any
                  pallet / location / product, flag a wrong
                  placement, and submit a Request Move for super-
                  admin approval. Independent of Stock Check — a
                  user can have Audit without Stock Check. */}
              <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-amber-200 bg-amber-50/40 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!editing.allow_floor_audit}
                  onChange={(e) => setEditing({ ...editing, allow_floor_audit: e.target.checked })}
                  className="w-4 h-4 accent-amber-600"
                  data-testid="admin-user-modal-allow-floor-audit"
                />
                <span className="text-sm text-[#0F172A]">
                  Allow Audit on Floor
                  <span className="block text-[11px] text-neutral-500 mt-0.5">
                    Enable the &ldquo;Audit&rdquo; tab on Floor Mode — search any pallet / location / product and submit a Request Move. Super admin reviews every request before the pallet physically relocates.
                  </span>
                </span>
              </label>

              {/* Feb 2026 v8 — Allow this warehouse user to delete
                  uploaded loading photos from the Floor Mode Loading
                  grid. Super admin toggles per user; the delete
                  button on each photo is gated on this flag. */}
              <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-red-200 bg-red-50/40 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!editing.allow_delete_loading_photos}
                  onChange={(e) => setEditing({ ...editing, allow_delete_loading_photos: e.target.checked })}
                  className="w-4 h-4 accent-red-600"
                  data-testid="admin-user-modal-allow-delete-loading-photos"
                />
                <span className="text-sm text-[#0F172A]">
                  Allow Delete Loading Photos
                  <span className="block text-[11px] text-neutral-500 mt-0.5">
                    Let this user delete uploaded loading photos from the Floor Mode Loading grid. Use for trusted supervisors who correct mis-uploaded photos.
                  </span>
                </span>
              </label>

              {/* Feb 2026 — Allow bulk delete of branch-uploaded receipt
                  documents on each order. Independent of the loading
                  photos flag above — split so ops can grant one
                  without the other. */}
              <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-red-200 bg-red-50/40 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!editing.allow_delete_received_documents}
                  onChange={(e) => setEditing({ ...editing, allow_delete_received_documents: e.target.checked })}
                  className="w-4 h-4 accent-red-600"
                  data-testid="admin-user-modal-allow-delete-received-documents"
                />
                <span className="text-sm text-[#0F172A]">
                  Allow Delete Received Documents
                  <span className="block text-[11px] text-neutral-500 mt-0.5">
                    Let this user bulk-delete branch-uploaded receipt documents (proof of delivery) on any order. Super admin already has this.
                  </span>
                </span>
              </label>

              {/* Feb 2026 — Per-user Floor-Mode capability toggles.
                  Grant Receiving / Put-Away / Loading tab access on
                  the Floor page independent of the user's warehouse
                  role. A picker with `allow_floor_receiving = true`
                  will see the Receiving tab and their assigned ASNs
                  will be surfaced there. */}
              <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-sky-200 bg-sky-50/40 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!editing.allow_floor_receiving}
                  onChange={(e) => setEditing({ ...editing, allow_floor_receiving: e.target.checked })}
                  className="w-4 h-4 accent-sky-600"
                  data-testid="admin-user-modal-allow-floor-receiving"
                />
                <span className="text-sm text-[#0F172A]">
                  Allow Floor Mode &mdash; Receiving
                  <span className="block text-[11px] text-neutral-500 mt-0.5">
                    Show the &ldquo;Receiving&rdquo; tab on the Floor page for this user so they can accept assigned ASNs and scan pallets on the dock. Works even if they don&rsquo;t hold a Manager / Floor In-charge role.
                  </span>
                </span>
              </label>

              <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-indigo-200 bg-indigo-50/40 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!editing.allow_floor_putaway}
                  onChange={(e) => setEditing({ ...editing, allow_floor_putaway: e.target.checked })}
                  className="w-4 h-4 accent-indigo-600"
                  data-testid="admin-user-modal-allow-floor-putaway"
                />
                <span className="text-sm text-[#0F172A]">
                  Allow Floor Mode &mdash; Put-Away
                  <span className="block text-[11px] text-neutral-500 mt-0.5">
                    Show the &ldquo;Put-away&rdquo; tab on the Floor page for this user so they can confirm assigned put-away tasks. Works even without a Manager / Floor In-charge role.
                  </span>
                </span>
              </label>

              <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-emerald-200 bg-emerald-50/40 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!editing.allow_floor_loading}
                  onChange={(e) => setEditing({ ...editing, allow_floor_loading: e.target.checked })}
                  className="w-4 h-4 accent-emerald-600"
                  data-testid="admin-user-modal-allow-floor-loading"
                />
                <span className="text-sm text-[#0F172A]">
                  Allow Floor Mode &mdash; Loading
                  <span className="block text-[11px] text-neutral-500 mt-0.5">
                    Show the &ldquo;Loading&rdquo; tab on the Floor page for this user so they can upload goods + seal photos to complete loading tasks. Works even without a Manager / Floor In-charge role.
                  </span>
                </span>
              </label>

              {/* Feb 2026 — ASN task-assignment toggle. Grants access to
                  the "Assign receiving task" and "Assign put-away tasks"
                  dropdowns on the Admin Goods Receiving page. Also
                  implicitly grants `asn.view` + `asn.assign_putaway`
                  so the user sees the Goods Receiving sidebar entry. */}
              <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-amber-200 bg-amber-50/40 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!editing.allow_asn_task_assignment}
                  onChange={(e) => setEditing({ ...editing, allow_asn_task_assignment: e.target.checked })}
                  className="w-4 h-4 accent-amber-600"
                  data-testid="admin-user-modal-allow-asn-task-assignment"
                />
                <span className="text-sm text-[#0F172A]">
                  Allow ASN Task Assignment (Receiving + Put-Away)
                  <span className="block text-[11px] text-neutral-500 mt-0.5">
                    Let this user assign floor staff to <b>receiving</b> and <b>put-away</b> tasks on the Goods Receiving page. Implicitly grants ASN view + assign-put-away permissions so nothing else needs to be ticked.
                  </span>
                </span>
              </label>

              {/* Feb 2026 — Day Close Reports toggle. Grants access to the
                  Day Close Report page + PDF (implicit `warehouse.reports`
                  grant) without needing the full RBAC tree ticked. */}
              <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-sky-200 bg-sky-50/40 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!editing.allow_day_close_reports}
                  onChange={(e) => setEditing({ ...editing, allow_day_close_reports: e.target.checked })}
                  className="w-4 h-4 accent-sky-600"
                  data-testid="admin-user-modal-allow-day-close-reports"
                />
                <span className="text-sm text-[#0F172A]">
                  Allow Day Close Reports
                  <span className="block text-[11px] text-neutral-500 mt-0.5">
                    Let this user open the <b>Day Close</b> page in the sidebar and download the daily Day Close Report PDF. Implicitly grants the <code className="px-1 rounded bg-neutral-100 text-[10px]">warehouse.reports</code> permission so nothing else needs to be ticked.
                  </span>
                </span>
              </label>

              {/* Feb 2026 — Manual allocation toggle. Grants access to the
                  Manual Allocate button + Pick List inline edit (implicit
                  `warehouse.manual_allocate` grant). */}
              <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-emerald-200 bg-emerald-50/40 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!editing.allow_manual_allocate}
                  onChange={(e) => setEditing({ ...editing, allow_manual_allocate: e.target.checked })}
                  className="w-4 h-4 accent-emerald-600"
                  data-testid="admin-user-modal-allow-manual-allocate"
                />
                <span className="text-sm text-[#0F172A]">
                  Allow Manual Allocation & Pick List Inline Edit
                  <span className="block text-[11px] text-neutral-500 mt-0.5">
                    Let this user open the <b>Manual Allocate</b> modal on any confirmed order and inline-edit qty / remove lines on the pick list. Implicitly grants the <code className="px-1 rounded bg-neutral-100 text-[10px]">warehouse.manual_allocate</code> permission.
                  </span>
                </span>
              </label>
            </div>

            <div className="hidden md:flex items-center justify-end gap-2 p-5 border-t border-neutral-100 sticky bottom-0 bg-white">
              <button
                type="button"
                onClick={close}
                className="rounded-full border border-neutral-300 text-neutral-700 px-4 py-2 text-xs font-bold inline-flex items-center gap-1.5 hover:bg-neutral-50"
              >
                <RotateCcw className="w-4 h-4" /> Cancel
              </button>
              <button
                type="button"
                onClick={save}
                disabled={busy}
                className="rounded-full bg-[#0F172A] hover:bg-black disabled:opacity-50 text-white px-4 py-2 text-xs font-bold inline-flex items-center gap-1.5"
                data-testid="admin-user-modal-save"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {editing.id ? 'Save changes' : 'Create admin'}
              </button>
            </div>

            {/* Mobile fixed bottom action bar */}
            <MobileFixedActions
              primaryLabel={editing.id ? 'Save changes' : 'Create admin'}
              onPrimary={save}
              primaryDisabled={busy}
              primaryLoading={busy}
              primaryTestId="admin-user-modal-save-mobile"
              secondaryLabel="Cancel"
              onSecondary={close}
            />
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminUsers;
